'use client';

import { useState, useMemo, useCallback } from 'react';
import { Drawer, Button, Select, Badge } from '@/shared/ui';
import {
  Calendar, Users, Clock, UtensilsCrossed,
  CheckCircle2, Star, MessageSquare, Armchair,
  Flame, Shirt, Banknote, AlertTriangle,
} from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useLocalization } from '@/core/hooks/useLocalization';
import type { Restaurant, TimeSlot, SeatingPreference } from '../types/dining.types';
import {
  GUEST_COUNT_OPTIONS,
  OCCASION_OPTIONS,
  SEATING_OPTIONS,
  TABLE_RESERVE_CHARGE,
  OCCASION_KEYS,
  SEATING_KEYS,
} from './dining.constants';
import { useDiningData, useCreateDiningReservation } from '../hooks/useDining';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Props                                                                     */
/* -------------------------------------------------------------------------- */

interface DiningReservationDrawerProps {
  restaurant: Restaurant;
  checkIn?: string;
  checkOut?: string;
  isHotelGuest?: boolean;
  onClose: () => void;
  /** Pre-fill for modify flow */
  editMode?: {
    date: string;
    time: string;
    guests: number;
    occasion: string;
    seatingPreference: SeatingPreference;
    specialRequests: string;
  };
}

/* -------------------------------------------------------------------------- */
/*  SectionLabel                                                              */
/* -------------------------------------------------------------------------- */

function SectionLabel({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-3.5 w-3.5 text-hms-gold" />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
        {label}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  DiningReservationDrawer                                                   */
/* -------------------------------------------------------------------------- */

export function DiningReservationDrawer({
  restaurant,
  checkIn,
  checkOut,
  isHotelGuest = true,
  onClose,
  editMode,
}: DiningReservationDrawerProps) {
  const { t } = useTranslation();
  const { formatMoney } = useLocalization();
  const { data: diningData } = useDiningData();
  const createReservation = useCreateDiningReservation();
  const allTimeSlots = useMemo(
    () => (diningData?.data as unknown as { timeSlots?: Record<string, TimeSlot[]> })?.timeSlots ?? {},
    [diningData?.data],
  );
  const [date, setDate] = useState(editMode?.date || checkIn || '');
  const [guests, setGuests] = useState(editMode?.guests || 2);
  const [occasion, setOccasion] = useState(editMode?.occasion || '');
  const [seating, setSeating] = useState<SeatingPreference | ''>(editMode?.seatingPreference || '');
  const [selectedTime, setSelectedTime] = useState(editMode?.time || '');
  const [guestName, setGuestName] = useState('');
  const [phone, setPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState(editMode?.specialRequests || '');
  const [confirmed, setConfirmed] = useState(false);
  const [reservationError, setReservationError] = useState(false);

  const [confirmationId, setConfirmationId] = useState('');

  const timeSlots = useMemo(
    () => allTimeSlots[restaurant.id] || [],
    [allTimeSlots, restaurant.id],
  );
  const availableSeating = restaurant.seatingOptions || [];

  const canConfirm = date && selectedTime && guestName.trim() && phone.trim();

  /* ── Charge calculation for non-hotel guests ──────────────── */
  const isPeakTime = useMemo(() => {
    if (!selectedTime) return false;
    const slot = timeSlots.find((s) => s.time === selectedTime);
    return slot?.isPeak ?? false;
  }, [selectedTime, timeSlots]);

  const reservationCharge = useMemo(() => {
    if (isHotelGuest || !TABLE_RESERVE_CHARGE.enabled) return 0;
    const base = TABLE_RESERVE_CHARGE.chargeType === 'per-person'
      ? TABLE_RESERVE_CHARGE.perPersonAmount * guests
      : TABLE_RESERVE_CHARGE.fixedAmount;
    const surcharge = TABLE_RESERVE_CHARGE.peakEnabled && isPeakTime
      ? (TABLE_RESERVE_CHARGE.chargeType === 'per-person'
        ? TABLE_RESERVE_CHARGE.peakSurcharge * guests
        : TABLE_RESERVE_CHARGE.peakSurcharge)
      : 0;
    return base + surcharge;
  }, [isHotelGuest, guests, isPeakTime]);

  const handleConfirm = useCallback(() => {
    createReservation.mutate({
      outletId: restaurant.id,
      guestId: guestName,
      partySize: guests,
      date,
      time: selectedTime,
      specialRequests: specialRequests || undefined,
    }, {
      onSuccess: (res) => {
        setConfirmationId(res?.data?.id || '');
        setConfirmed(true);
      },
      onError: () => {
        setReservationError(true);
      },
    });
  }, [createReservation, restaurant.id, guestName, guests, date, selectedTime, specialRequests]);

  const inputClass =
    'h-10 w-full rounded-lg border border-hms-border bg-hms-surface px-3 text-[13px] text-hms-text outline-none focus:border-hms-gold/50 transition-colors';
  const labelClass =
    'block text-[11px] font-medium text-hms-textDim uppercase tracking-wider mb-1.5';

  /* ── Error view ── */
  if (reservationError) {
    return (
      <Drawer id="dining-res-drawer" open onClose={onClose} title={t('dining.reservationFailed')} size="xl">
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-hms-red/10">
            <AlertTriangle className="h-8 w-8 text-hms-red" />
          </div>
          <p id="dining-res-error" className="text-lg font-semibold text-hms-text">{t('dining.reservationFailedMessage')}</p>
          <p className="text-[13px] text-hms-textSub text-center">{t('dining.reservationFailedDesc')}</p>
          <Button id="dining-res-retry" onClick={() => setReservationError(false)}>{t('common.tryAgain')}</Button>
        </div>
      </Drawer>
    );
  }

  /* ── Confirmation view ── */
  if (confirmed) {
    return (
      <Drawer
        id="dining-res-drawer"
        open
        onClose={onClose}
        title={t('dining.reservationConfirmed')}
        size="xl"
        hideCloseButton
        footer={
          <div className="flex justify-end">
            <Button id="dining-res-done" variant="secondary" onClick={onClose}>
              {t('common.done')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-hms-green/10">
            <CheckCircle2 className="h-8 w-8 text-hms-green" />
          </div>
            <p id="dining-res-success" className="text-lg font-semibold text-hms-text">
              {t('dining.tableReserved')}
            </p>
            <p className="text-[13px] text-hms-textSub">
              {t('dining.form.confirmationLabel')}:{' '}
              <span className="font-mono font-semibold text-hms-gold">{confirmationId}</span>
            </p>

          <div className="mt-2 w-full rounded-xl border border-hms-border bg-hms-surface p-5 space-y-3">
            <div className="flex items-center gap-2.5 text-[13px]">
              <UtensilsCrossed className="h-4 w-4 text-hms-gold" />
              <span className="font-medium text-hms-text">{restaurant.name}</span>
              <span className="ml-auto rounded-md bg-hms-gold/10 px-2 py-0.5 text-[10px] font-semibold text-hms-gold">
                {restaurant.type}
              </span>
            </div>
            <div className="h-px bg-hms-border" />
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-[12px]">
                <Calendar className="h-3.5 w-3.5 text-hms-textDim" />
                <span className="text-hms-textSub">{date}</span>
              </div>
              <div className="flex items-center gap-2 text-[12px]">
                <Clock className="h-3.5 w-3.5 text-hms-textDim" />
                <span className="text-hms-textSub">{selectedTime}</span>
              </div>
              <div className="flex items-center gap-2 text-[12px]">
                <Users className="h-3.5 w-3.5 text-hms-textDim" />
                <span className="text-hms-textSub">{guests} {guests > 1 ? t('dining.guests') : t('dining.guest')}</span>
              </div>
              {seating && (
                <div className="flex items-center gap-2 text-[12px]">
                  <Armchair className="h-3.5 w-3.5 text-hms-textDim" />
                  <span className="text-hms-textSub">{seating}</span>
                </div>
              )}
            </div>
            {reservationCharge > 0 && (
              <>
                <div className="h-px bg-hms-border" />
                <div className="flex items-center gap-2 text-[12px]">
                  <Banknote className="h-3.5 w-3.5 text-hms-gold" />
                  <span className="text-hms-gold font-medium">
                    {formatMoney(reservationCharge)} {t('dining.reservationFee')}
                  </span>
                </div>
              </>
            )}
            {occasion && (
              <>
                <div className="h-px bg-hms-border" />
                <p className="text-[11px] text-hms-textDim">
                  {t('dining.occasion')}: <span className="text-hms-textSub">{occasion}</span>
                </p>
              </>
            )}
          </div>

          {isHotelGuest ? (
            <p className="mt-1 text-[11px] text-hms-teal text-center">
              {t('dining.complimentaryStay')}
            </p>
          ) : reservationCharge > 0 ? (
            <p id="dining-res-charge-confirmed" className="mt-1 text-[11px] text-hms-gold text-center">
              {t('dining.reservationFeeOf')} {formatMoney(reservationCharge)} {t('dining.chargedAtRestaurant')}
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-hms-teal text-center">
              {t('dining.noAdditionalCharges')}
            </p>
          )}
        </div>
      </Drawer>
    );
  }

  /* ── Reservation form ── */
  return (
    <Drawer
      id="dining-res-drawer"
      open
      onClose={onClose}
        title={`${t('dining.reserveTitle')} — ${restaurant.name}`}
      description={`${restaurant.type} · ${restaurant.cuisine}`}
      size="xl"
      hideCloseButton
      footer={
        <div className="flex items-center justify-between">
          <Button id="dining-res-cancel" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            id="dining-res-confirm"
            variant="primary"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {t('dining.confirmReservation')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* ── Restaurant info banner ── */}
        <div className="rounded-xl border border-hms-border bg-hms-surface p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-hms-gold/10">
                <UtensilsCrossed className="h-5 w-5 text-hms-gold" />
              </div>
              <div>
                <p className="text-[13px] font-semibold text-hms-text">{restaurant.name}</p>
                <p className="text-[11px] text-hms-textDim">{restaurant.cuisine}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-hms-textDim">
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 text-hms-gold" /> {restaurant.rating}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> {restaurant.openTime} - {restaurant.closeTime}
              </span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {restaurant.dressCode && (
              <span className="inline-flex items-center gap-1 rounded-md bg-hms-gold/5 border border-hms-gold/15 px-2 py-0.5 text-[10px] font-medium text-hms-gold">
                <Shirt className="h-2.5 w-2.5" /> {restaurant.dressCode}
              </span>
            )}
            {restaurant.dietaryTags.map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-hms-teal/5 border border-hms-teal/15 px-2 py-0.5 text-[10px] font-medium text-hms-teal"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* ── Non-guest charge notice ── */}
        {!isHotelGuest && TABLE_RESERVE_CHARGE.enabled && (
          <div id="dining-res-charge-notice" className="p-3.5 rounded-xl border border-hms-amber/20 bg-hms-amber/5">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-hms-amber shrink-0 mt-0.5" />
              <div>
                <p className="text-[12px] font-medium text-hms-text">{t('dining.reservationFeeApplies')}</p>
                <p className="text-[11px] text-hms-textDim mt-0.5 leading-relaxed">
                  {t('dining.feeOf')} {formatMoney(TABLE_RESERVE_CHARGE.chargeType === 'per-person' ? TABLE_RESERVE_CHARGE.perPersonAmount : TABLE_RESERVE_CHARGE.fixedAmount)}
                  {TABLE_RESERVE_CHARGE.chargeType === 'per-person' ? ` ${t('dining.perPerson')}` : ` ${t('dining.perReservation')}`} {t('dining.appliesForNonGuests')}
                  {TABLE_RESERVE_CHARGE.peakEnabled && ` ${t('dining.peakHoursSurcharge')}: +${formatMoney(TABLE_RESERVE_CHARGE.peakSurcharge)} ${t('dining.surcharge')}.`}
                </p>
                <p className="text-[10px] text-hms-teal mt-1">
                  {t('dining.hotelGuestsDineFree')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Section 1: Date & Party ── */}
        <div>
          <SectionLabel icon={Calendar} label={t('dining.dateAndPartySize')} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="dining-res-date" className={labelClass}>{t('dining.form.dateLabel')}</label>
              <input
                id="dining-res-date"
                type="date"
                value={date}
                min={checkIn || undefined}
                max={checkOut || undefined}
                onChange={(e) => setDate(e.target.value)}
                className={inputClass}
              />
                {checkIn && checkOut && (
                  <p className="mt-1 text-[10px] text-hms-textDim">
                    {t('dining.form.stayLabel')}: {checkIn} to {checkOut}
                  </p>
                )}
            </div>
            <div>
              <Select
                id="dining-res-guests"
                label={t('dining.numberOfGuests')}
                value={String(guests)}
                onChange={(e) => setGuests(Number(e.target.value))}
                options={GUEST_COUNT_OPTIONS.map((n) => ({
                  label: `${n} ${n === 1 ? t('dining.guest') : t('dining.guests')}`,
                  value: String(n),
                }))}
              />
            </div>
            <div>
              <Select
                id="dining-res-occasion"
                label={t('dining.occasion')}
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                placeholder={t('dining.selectOccasionOptional')}
                options={OCCASION_OPTIONS.filter((o) => o.value !== '').map(o => ({
                  label: t(OCCASION_KEYS[o.label] || o.label),
                  value: o.value,
                }))}
              />
            </div>
            <div>
              <Select
                id="dining-res-seating"
                label={t('dining.seatingPreference')}
                value={seating}
                onChange={(e) => setSeating(e.target.value as SeatingPreference)}
                placeholder={t('dining.anySeating')}
                options={availableSeating.map((s) => {
                  const opt = SEATING_OPTIONS.find((o) => o.value === s);
                  return { label: opt ? t(SEATING_KEYS[opt.label] || opt.label) : s, value: s };
                })}
              />
            </div>
          </div>
        </div>

        {/* ── Section 2: Time Slots ── */}
        <div>
          <SectionLabel icon={Clock} label={t('dining.availableTimeSlots')} />
          {!date ? (
            <p className="text-[12px] text-hms-textDim">{t('dining.selectDateForSlots')}</p>
          ) : (
            <>
              <p className="mb-3 text-[12px] text-hms-textSub">
                Showing availability for {restaurant.name} on {date}
              </p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {timeSlots.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    id={`dining-res-slot-${slot.time.replace(/[: ]/g, '')}`}
                    disabled={!slot.available}
                    onClick={() => setSelectedTime(slot.time)}
                    className={cn(
                      'relative flex flex-col items-center rounded-lg border px-2 py-2.5 text-center transition-all',
                      !slot.available &&
                        'cursor-not-allowed border-hms-border/50 bg-hms-surface/50 opacity-40',
                      slot.available && selectedTime !== slot.time &&
                        'border-hms-border bg-hms-surface text-hms-textSub hover:border-hms-gold/30 hover:bg-hms-gold/5',
                      selectedTime === slot.time &&
                        'border-hms-gold bg-hms-gold/10 text-hms-gold ring-1 ring-hms-gold/30',
                      slot.isPeak && slot.available && selectedTime !== slot.time &&
                        'border-hms-gold/20',
                    )}
                  >
                    <span className="text-[12px] font-medium">{slot.time}</span>
                    {slot.available && (
                      <span
                        className={cn(
                          'mt-0.5 text-[9px] font-medium',
                          slot.tablesLeft <= 2 ? 'text-hms-red' :
                          slot.tablesLeft <= 5 ? 'text-hms-amber' :
                          'text-hms-textDim',
                        )}
                      >
                        {slot.tablesLeft} {t('dining.left')}
                      </span>
                    )}
                    {slot.isPeak && slot.available && (
                      <Flame className="absolute -top-1 -right-1 h-3 w-3 text-hms-gold" />
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-2.5 flex items-center gap-4 text-[10px] text-hms-textDim">
                <span className="flex items-center gap-1">
                  <Flame className="h-2.5 w-2.5 text-hms-gold" /> {t('dining.peakHour')}
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-hms-red" /> {t('dining.lastTables')}
                </span>
              </div>
            </>
          )}
        </div>

        {/* ── Section 3: Guest Details ── */}
        <div>
          <SectionLabel icon={Users} label={t('dining.guestDetails')} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="dining-res-name" className={labelClass}>{t('dining.guestName')}</label>
              <input
                id="dining-res-name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="dining-res-phone" className={labelClass}>{t('chainStaff.phone')}</label>
              <input
                id="dining-res-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div className="mt-3">
            <label htmlFor="dining-res-requests" className={labelClass}>
              {t('dining.specialRequests')}
            </label>
            <textarea
              id="dining-res-requests"
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              rows={2}
              placeholder={t('dining.dietaryRequirementsPlaceholder')}
              className="w-full rounded-lg border border-hms-border bg-hms-surface px-3 py-2 text-[13px] text-hms-text outline-none focus:border-hms-gold/50 transition-colors resize-none"
            />
          </div>
        </div>

        {/* ── Section 4: Summary ── */}
        {date && selectedTime && (
          <div>
            <SectionLabel icon={MessageSquare} label={t('dining.reservationSummary')} />
            <div className="rounded-xl border border-hms-gold/20 bg-hms-gold/[0.03] p-4 space-y-2">
              <div className="flex justify-between text-[12px]">
                <span className="text-hms-textDim">{t('dining.restaurant')}</span>
                <span className="font-medium text-hms-text">{restaurant.name}</span>
              </div>
               <div className="flex justify-between text-[12px]">
                 <span className="text-hms-textDim">{t('labels.date')}</span>
                 <span className="text-hms-text">{date}</span>
               </div>
               <div className="flex justify-between text-[12px]">
                 <span className="text-hms-textDim">{t('labels.time')}</span>
                 <span className="font-medium text-hms-gold">{selectedTime}</span>
               </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-hms-textDim">{t('dining.guests')}</span>
                <span className="text-hms-text">{guests}</span>
              </div>
              {seating && (
                <div className="flex justify-between text-[12px]">
                  <span className="text-hms-textDim">{t('dining.seating')}</span>
                  <span className="text-hms-text">{seating}</span>
                </div>
              )}
              {occasion && (
                <div className="flex justify-between text-[12px]">
                  <span className="text-hms-textDim">{t('dining.occasion')}</span>
                  <span className="text-hms-text">{occasion}</span>
                </div>
              )}
              <div className="h-px bg-hms-border/50 my-1" />
              {reservationCharge > 0 ? (
                <div id="dining-res-charge-line" className="flex justify-between text-[12px]">
                  <span className="text-hms-textDim flex items-center gap-1">
                    {t('dining.reservationFee')}
                    {isPeakTime && <Badge variant="gold">{t('dining.peak')}</Badge>}
                  </span>
                  <span className="text-hms-gold font-semibold">{formatMoney(reservationCharge)}</span>
                </div>
              ) : isHotelGuest ? (
                <div id="dining-res-complimentary" className="flex justify-between text-[12px]">
                  <span className="text-hms-textDim">{t('dining.reservationFee')}</span>
                  <span className="text-hms-green font-medium">{t('dining.complimentary')}</span>
                </div>
              ) : (
                <p className="text-[11px] text-hms-teal font-medium text-center">
                  {t('dining.noAdditionalCharges')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}
